import { billingStatusAtom, subscriptionAtom, subscriptionFeaturesAtom } from '@/atoms/subscription.atom';
import { execute } from '@/graphql/execute';
import {
  BillingInterval,
  CollectionMethod,
  SubscriptionDetailsQuery,
  SubscriptionInfo,
  SubscriptionPlan,
} from '@/graphql/graphql';
import {
  applySubscriptionDiscountMutation,
  cancelSubscriptionMutation,
  changeSubscriptionPlanMutation,
  createBillingPortalSessionMutation,
  createSubscriptionMutation,
  reactivateSubscriptionMutation,
  removeSubscriptionDiscountMutation,
} from '@/mutations/subscription.mutation';
import {
  organizationWithSubscriptionQuery,
  subscriptionDetailsQuery,
  subscriptionPaymentUrlQuery,
} from '@/queries/subscription.query';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useOrganization } from './useOrganizations';

export interface CreateSubscriptionParams {
  plan: SubscriptionPlan;
  billingInterval: BillingInterval;
  collectionMethod: CollectionMethod;
  promoCode?: string;
  successUrl: string;
  cancelUrl: string;
  userEmail?: string;
}

export const useSubscription = () => {
  const organization = useOrganization();
  const organizationId = organization?.id;
  const [subscription, setSubscription] = useAtom(subscriptionAtom);
  const features = useAtomValue(subscriptionFeaturesAtom);
  const billingStatus = useAtomValue(billingStatusAtom);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<SubscriptionDetailsQuery['subscriptionDetails'] | null>(null);

  // Fetch subscription info for the current organization
  const fetchSubscription = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await execute(organizationWithSubscriptionQuery, { id: organizationId });
      if (data.organization?.subscription) {
        setSubscription(data.organization.subscription as SubscriptionInfo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription');
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  }, [organizationId, setSubscription]);

  // Auto-fetch subscription on mount when organizationId is available
  useEffect(() => {
    if (organizationId && !initialLoadComplete) {
      fetchSubscription();
    }
  }, [organizationId, initialLoadComplete, fetchSubscription]);

  // Fetch detailed subscription info including invoices
  const fetchSubscriptionDetails = useCallback(async () => {
    if (!organizationId) return null;

    setLoading(true);
    setError(null);

    try {
      const data = await execute(subscriptionDetailsQuery, { organizationId });
      setDetails(data.subscriptionDetails);
      if (data.subscriptionDetails?.info) {
        setSubscription(data.subscriptionDetails.info as SubscriptionInfo);
      }
      return data.subscriptionDetails;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription details');
      return null;
    } finally {
      setLoading(false);
    }
  }, [organizationId, setSubscription]);

  // Create a new subscription
  const createSubscription = useCallback(
    async (params: CreateSubscriptionParams) => {
      if (!organizationId) throw new Error('Organization ID is required');

      setLoading(true);
      setError(null);

      try {
        const result = await execute(createSubscriptionMutation, {
          input: {
            organizationId,
            plan: params.plan,
            billingInterval: params.billingInterval,
            collectionMethod: params.collectionMethod,
            promoCode: params.promoCode,
            successUrl: params.successUrl,
            cancelUrl: params.cancelUrl,
            userEmail: params.userEmail,
          },
        });

        return result.createSubscription;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create subscription';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId],
  );

  // Change subscription plan
  const changePlan = useCallback(
    async (plan: SubscriptionPlan) => {
      if (!organizationId) throw new Error('Organization ID is required');

      setLoading(true);
      setError(null);

      try {
        await execute(changeSubscriptionPlanMutation, {
          input: { organizationId, plan },
        });
        await fetchSubscription();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to change plan';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId, fetchSubscription],
  );

  // Cancel subscription
  const cancelSubscription = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      await execute(cancelSubscriptionMutation, { organizationId });
      await fetchSubscription();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel subscription';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchSubscription]);

  // Reactivate subscription
  const reactivateSubscription = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      await execute(reactivateSubscriptionMutation, { organizationId });
      await fetchSubscription();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reactivate subscription';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchSubscription]);

  // Apply discount/promo code
  const applyDiscount = useCallback(
    async (promoCode: string) => {
      if (!organizationId) throw new Error('Organization ID is required');

      setLoading(true);
      setError(null);

      try {
        await execute(applySubscriptionDiscountMutation, {
          input: { organizationId, promoCode },
        });
        await fetchSubscriptionDetails();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to apply discount';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId, fetchSubscriptionDetails],
  );

  // Remove discount
  const removeDiscount = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      await execute(removeSubscriptionDiscountMutation, { organizationId });
      await fetchSubscriptionDetails();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove discount';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchSubscriptionDetails]);

  // Open billing portal to manage payment methods and billing details
  const openBillingPortal = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      const returnUrl = window.location.href;
      const result = await execute(createBillingPortalSessionMutation, {
        organizationId,
        returnUrl,
      });
      // Redirect to Stripe Billing Portal
      window.location.href = result.createBillingPortalSession;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open billing portal';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Send the user to Stripe's hosted page for the outstanding invoice so they
  // can finish an abandoned checkout or settle a failed renewal. Resolves to
  // false (after refreshing the subscription) when nothing is outstanding —
  // e.g. the webhook already moved the org on.
  const openPendingPayment = useCallback(async () => {
    if (!organizationId) throw new Error('Organization ID is required');

    setLoading(true);
    setError(null);

    try {
      const result = await execute(subscriptionPaymentUrlQuery, { organizationId });
      const url = result.subscriptionPaymentUrl;
      if (!url) {
        await fetchSubscription();
        return false;
      }
      window.location.href = url;
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open payment page';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchSubscription]);

  return {
    // State
    subscription,
    features,
    billingStatus,
    details,
    loading,
    initialLoadComplete,
    error,

    // Actions
    fetchSubscription,
    fetchSubscriptionDetails,
    createSubscription,
    changePlan,
    cancelSubscription,
    reactivateSubscription,
    applyDiscount,
    removeDiscount,
    openBillingPortal,
    openPendingPayment,
  };
};
